import { useTranslation } from 'react-i18next';

import Icon from '@ant-design/icons';

import * as St from './DesignSectionCard.styles';

import {
  type DraggableAppliance,
  useDragAndDrop,
} from '~/core/hooks/useDragAndDrop';
import Icon3dView from '~assets/icons/Icon3dView.svg';
import { type PlainCardProps } from '~atoms/PlainCard';

type DesignSectionCardProps<T> = Omit<PlainCardProps, 'withHover' | 'ref'> & {
  title: string;
  image: string;
  onClick3DBtn?: () => void;
  tooltipText?: string;
  dndArgs?: {
    appliance: T;
    onDragEnd?: () => void;
  };
  trendLabel?: string | null;
};

const DesignSectionCard = <T extends DraggableAppliance>({
  title,
  image,
  onClick3DBtn,
  tooltipText,
  children,
  dndArgs,
  trendLabel,
  ...props
}: DesignSectionCardProps<T>) => {
  const { t } = useTranslation();
  const isDraggable = Boolean(dndArgs?.appliance);
  const { handleDrag, handleDragEnd, handleDragStart } = useDragAndDrop();

  return (
    <St.Container withHover {...props}>
      <St.ImgContainer draggable={isDraggable}>
        {trendLabel && <St.OnTrend>{t(trendLabel).toUpperCase()}</St.OnTrend>}
        <St.Img
          src={image}
          onDrag={handleDrag}
          onDragStart={
            dndArgs?.appliance
              ? handleDragStart({ appliance: dndArgs.appliance })
              : undefined
          }
          onDragEnd={() => handleDragEnd(dndArgs?.onDragEnd)}
          draggable={isDraggable}
        />
        {onClick3DBtn && (
          <St.Btn3DModal
            aria-label="3D"
            onClick={(event) => {
              event.stopPropagation();
              onClick3DBtn();
            }}
          >
            <Icon component={() => <Icon3dView />} />
          </St.Btn3DModal>
        )}
        {props.isSelected && tooltipText && (
          <St.Tooltip>{tooltipText}</St.Tooltip>
        )}
      </St.ImgContainer>
      <St.Title>{title}</St.Title>
      {children}
    </St.Container>
  );
};

export default DesignSectionCard;
